import { Component, OnDestroy, OnInit } from "@angular/core";
import * as signalr from "@microsoft/signalr";
import { PdfExportingHub } from "@main/pdf-exporting/api/pdf-exporting.hub";
import { PdfExportingService } from "@main/pdf-exporting/api/pdf-exporting.service";
import {
    PdfExportingEvent,
    PdfExportUpdateHandler,
    PdfExportSummaryUpdateHandler,
    PdfExportLoginSummary
} from "@main/pdf-exporting/pdf-exporting.models";
import { IHash } from "@bbwt/interfaces";

@Component({
    selector: "pdf-exporting",
    templateUrl: "./pdf-exporting.component.html",
    styleUrls: ["./pdf-exporting.component.scss"]
})
export class PdfExportingComponent implements OnInit, OnDestroy {
    // Summary data by processor type.
    protected summaries: IHash<PdfExportLoginSummary> = {};

    // Summary visibility management.
    protected collapsed: boolean = false;

    protected totalItems: number = 0;

    // Pdf exporting settings.
    private pdfExportingHubConnection: signalr.HubConnection;

    constructor(private pdfExportingService: PdfExportingService,
                private pdfExportingHub: PdfExportingHub) {
    }

    public get visible(): boolean {
        return this.totalItems > 0;
    }

    public ngOnInit(): void {
        this.setupAutoDownload();
    }

    public ngOnDestroy(): void {
        this.clearAutoDownload();
    }

    private setupAutoDownload(): void {
        if (!!this.pdfExportingHubConnection) this.clearAutoDownload();

        const listener: string = PdfExportingComponent.name;
        const downloadHandler: PdfExportUpdateHandler = this.downloadPdf.bind(this);
        const summaryHandler: PdfExportSummaryUpdateHandler = this.refreshSummary.bind(this);

        this.pdfExportingHubConnection = this.pdfExportingHub.getListeningConnection([
            { event: PdfExportingEvent.ExportingFinished, handler: downloadHandler, listener: listener },
            { event: PdfExportingEvent.ExportingQueueUpdated, handler: summaryHandler, listener: listener }
        ]);
    }

    private clearAutoDownload(): void {
        if (!this.pdfExportingHubConnection) return;

        this.pdfExportingHub.releaseConnection(this.pdfExportingHubConnection);
    }

    private downloadPdf(id: string, name?: string): void {
        this.pdfExportingService.downloadExportedFile(id, name).then();
    }

    private refreshSummary(summary: PdfExportLoginSummary): void {
        if (!summary) return;

        if (summary.processingItems + summary.pendingItems > 0) {
            // If the new summary is not empty, replace the old version with this one.
            this.summaries[summary.processorType] = summary;

            // Otherwise, remove the old version of the summary.
        } else delete this.summaries[summary.processorType];

        this.totalItems = this.getSummaryTotalItems();

        // Force change detection in the summary component.
        this.summaries = { ...this.summaries };
    }

    private getSummaryTotalItems(): number {
        const summaries: PdfExportLoginSummary[] = Object.values(this.summaries);

        // Sum the total items of each processor-type summary.
        return summaries.reduce((total: number, summary: PdfExportLoginSummary): number =>
            total + summary.processingItems + summary.pendingItems, 0);
    }

    protected expandSummary(): void {
        this.collapsed = false;
    }

    protected hideSummary(): void {
        this.collapsed = true;
    }
}